import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import TaggedPermissionList, {
  TaggedPermissionItem,
} from "@/components/TaggedPermissionList/TaggedPermissionList";
import PermissionOverridesEditor from "@/components/PermissionOverridesEditor/PermissionOverridesEditor";
import { useNotification } from "@/context/NotificationContext";
import {
  CompanyPermissionOverride,
  fetchPermissionsForCompany,
  PermissionForDropdown,
  StagePermissionForDisplay,
} from "@/modules/administration/features/UserManagement/api/fetchPermissionsForCompany";
import { addCompanyPermissionOverride } from "../../api/addCompanyPermissionOverride";
import { removeCompanyPermissionOverride } from "../../api/removeCompanyPermissionOverride";

interface CompanyPermissionsCardProps {
  companyUuid: string;
  /**
   * Bump to force a refetch — e.g. after a company save that may have changed
   * the stage, so the company access list reflects the new stage.
   */
  refreshKey?: number;
}

const CompanyPermissionsCard: React.FC<CompanyPermissionsCardProps> = ({
  companyUuid,
  refreshKey = 0,
}) => {
  const { showNotification } = useNotification();

  const [availablePermissions, setAvailablePermissions] = useState<
    PermissionForDropdown[]
  >([]);
  const [stagePermissions, setStagePermissions] = useState<
    StagePermissionForDisplay[]
  >([]);
  const [overrides, setOverrides] = useState<CompanyPermissionOverride[]>([]);
  const [selectedPermissionId, setSelectedPermissionId] = useState<string>("");
  const [selectedOverrideType, setSelectedOverrideType] = useState<
    "grant" | "revoke"
  >("grant");
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [overrideLoading, setOverrideLoading] = useState(false);

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        setPermissionsLoading(true);
        const response = await fetchPermissionsForCompany(companyUuid);
        setAvailablePermissions(response.data.permissions);
        setStagePermissions(response.data.stagePermissions);
        setOverrides(response.data.companyPermissionOverrides);
      } catch (err) {
        console.error("Failed to load permissions:", err);
      } finally {
        setPermissionsLoading(false);
      }
    };

    loadPermissions();
  }, [companyUuid, refreshKey]);

  // Stage-granted permissions plus company grant overrides (revokes excluded),
  // deduped by permission id, sorted by label — same shape as the edit-user
  // effective list.
  const companyAccessItems = useMemo(() => {
    const merged = new Map<number, TaggedPermissionItem>();

    for (const permission of stagePermissions) {
      merged.set(permission.id, {
        id: permission.id,
        label: permission.label,
        tag: "granted by company stage",
      });
    }

    for (const override of overrides) {
      if (override.overrideType === "revoke") {
        merged.delete(override.permissionId);
      } else if (!merged.has(override.permissionId)) {
        merged.set(override.permissionId, {
          id: override.permissionId,
          label: override.permissionLabel,
          tag: "granted by company override",
        });
      }
    }

    return Array.from(merged.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [stagePermissions, overrides]);

  const handleAddOverride = async () => {
    if (!selectedPermissionId) return;

    try {
      setOverrideLoading(true);
      const response = await addCompanyPermissionOverride(companyUuid, {
        permissionId: parseInt(selectedPermissionId, 10),
        overrideType: selectedOverrideType,
      });

      // Update local state - replace if exists, add if new (upsert)
      setOverrides((prev) => {
        const existing = prev.findIndex(
          (o) => o.permissionId === response.data.override.permissionId,
        );
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = response.data.override;
          return updated;
        }
        return [...prev, response.data.override];
      });

      setSelectedPermissionId("");
      setSelectedOverrideType("grant");

      showNotification("Permission override added", "", "success");
    } catch {
      // Error handled by axios interceptor
    } finally {
      setOverrideLoading(false);
    }
  };

  const handleRemoveOverride = async (overrideId: number) => {
    try {
      setOverrideLoading(true);
      await removeCompanyPermissionOverride(companyUuid, overrideId);

      setOverrides((prev) => prev.filter((o) => o.id !== overrideId));

      showNotification("Permission override removed", "", "success");
    } catch {
      // Error handled by axios interceptor
    } finally {
      setOverrideLoading(false);
    }
  };

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Permissions</CardTitle>
        <CardDescription>
          Permissions granted by the company&apos;s stage, plus company-level
          grant/revoke overrides applied to every member.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Company access list (read-only) */}
        <div>
          <h3 className="font-medium text-sm text-gray-900 dark:text-white">
            This company has access to:
          </h3>
          {companyAccessItems.length > 0 ? (
            <div className="mt-2">
              <TaggedPermissionList items={companyAccessItems} />
            </div>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground italic">
              This company&apos;s stage and overrides grant no additional
              permissions.
            </p>
          )}
        </div>

        <PermissionOverridesEditor
          availablePermissions={availablePermissions}
          description="Grant or revoke specific permissions for every member of this company beyond their role and stage defaults."
          onAddOverride={handleAddOverride}
          onRemoveOverride={handleRemoveOverride}
          onSelectedOverrideTypeChange={setSelectedOverrideType}
          onSelectedPermissionIdChange={setSelectedPermissionId}
          overrideLoading={overrideLoading}
          overrides={overrides}
          permissionsLoading={permissionsLoading}
          selectedOverrideType={selectedOverrideType}
          selectedPermissionId={selectedPermissionId}
        />
      </CardContent>
    </Card>
  );
};

export default CompanyPermissionsCard;
